import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { DeviceService } from 'src/app/services/device/device.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { RecordBriefFeature } from '@omni/ai/features/record-brief.feature';
import { RecordRef, RecordKind } from '@omni/ai/features/record-ref';
import { Section } from '@omni/ai/features/card.schema';
import type { TokenUsage } from '@omni/ai/transports/ai-transport';
import { AssistantFeedbackService } from '@omni/ai/services/assistant-feedback.service';
import { NovaFeedbackFooterComponent, NovaFeedbackEvent } from '@omni/components/nova-voice/nova-feedback-footer/nova-feedback-footer.component';
import { SafePipe } from '@omni/pipes/safe.pipe';
import { parse as marked } from 'marked'
import { distanceInWordsToNow } from 'date-fns';

type ComponentState = 'collapsed' | 'minimized' | 'maximized';

type SummaryType = {
  progress: string;
  text: string | null;
  array: string[];
  markdown?: boolean;
  /** Whether the brief shows the AI-generated disclaimer. */
  showDisclaimer?: boolean;
  /** When this brief was generated (ISO), for the "generated X ago" label. */
  generatedAt?: string | null;
  /** Responses API id of the turn this brief came from, for feedback correlation. On a cache hit
   *  this is the ORIGINAL generation's id, carried forward by RecordBriefFeature. */
  responseId?: string | null;
  usage?: TokenUsage | null;
};
@Component({
  selector: 'app-spotlight-ai-summary',
  templateUrl: './spotlight-ai-summary.component.html',
  styleUrls: ['./spotlight-ai-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, SafePipe, NovaFeedbackFooterComponent]
})
export class SpotlightAiSummaryComponent {
  @Input() type: RecordKind;
  @Input() contactId: string;
  @Input() accountId: string;
  @Output() close = new EventEmitter<void>();

  #currentState: ComponentState = 'collapsed';
  #stateSubject = new BehaviorSubject<ComponentState>('collapsed');

  @HostBinding('class.collapsed')
  get isCollapsed(): boolean {
    return this.#currentState === 'collapsed';
  }

  get isMinimized(): boolean {
    return this.#currentState === 'minimized';
  }

  @HostBinding('class.maximized')
  get isMaximized(): boolean {
    return this.#currentState === 'maximized';
  }

  get isSpotlight(): boolean {
    return this.type === 'spotlight';
  }
  hasReadMore = false;
  readonly #el = inject(ElementRef);
  readonly #ngDestroy$ = new Subject<void>();
  readonly #resizeObserver = new Observable<void>(observer => {
    const resizeObserver = new ResizeObserver(() => {
      observer.next();
    });
    resizeObserver.observe(this.#el.nativeElement);

    return () => resizeObserver.disconnect();
  })
    .pipe(
      map(() => {
        const width = this.#el.nativeElement.clientWidth;
        const height = this.#el.nativeElement.clientHeight;
        return { width, height };
      }),
      distinctUntilChanged((prev, curr) => prev.width === curr.width && prev.height === curr.height)
    );



  loading = true;
  regenerating = false;
  /** ISO time the shown brief was generated; drives the "generated X ago" label. */
  generatedAt: string | null = null;
  /** Feedback-correlation metadata for the shown brief — null until a summary has loaded. */
  responseId: string | null = null;
  usage: TokenUsage | null = null;
  readonly #cd = inject(ChangeDetectorRef);
  readonly #deviceService = inject(DeviceService);
  readonly #recordBrief = inject(RecordBriefFeature);
  readonly #feedback = inject(AssistantFeedbackService);

  readonly isOffline$ = this.#deviceService.isOfflineObservable;
  /** Gates the controls that need the agent — only the regenerate button today. */
  readonly isOnline$ = this.isOffline$.pipe(map(isOffline => !isOffline));
  readonly #translate = inject(TranslateService);
  readonly #localization = inject(LocalizationService);
  showDisclaimer = false;

  /** Forces a fresh brief (bypasses the disk cache) — driven by the card's regenerate button. */
  readonly #regenerate$ = new Subject<void>();

  readonly summary$ = merge(
    // Auto-load on expand (cache-first); re-fires when the rep's connectivity changes while open,
    // so going offline falls back to the persisted brief and coming back online refreshes it.
    combineLatest([
      this.#stateSubject.pipe(
        startWith(this.#currentState),
        map((state) => state !== 'collapsed'),
        distinctUntilChanged(),
      ),
      this.isOffline$,
    ]).pipe(
      filter(([shouldLoad]) => shouldLoad),
      map(([, isOffline]) => ({ force: false, offline: isOffline })),
    ),
    // Regenerate button → force a fresh agent call + re-cache. Only reachable online.
    this.#regenerate$.pipe(map(() => ({ force: true, offline: false }))),
  ).pipe(
    tap(({ force }) => {
      if (force) this.regenerating = true;
      else this.loading = true;
      this.#cd.markForCheck();
    }),
    switchMap(({ force, offline }) => {
      // Territory (spotlight) summarises the whole territory, so it loads without a record id;
      // contact/account still need their id before we call the agent.
      const kind: RecordKind = this.type;
      const recordId =
        kind === 'contact' ? (this.contactId ?? '')
        : kind === 'account' ? (this.accountId ?? '')
        : '';
      return (kind === 'spotlight' || recordId)
        ? from(this.#loadSummary(kind, recordId, force, offline)).pipe(
            catchError(err => {
              console.error('[SpotlightAiSummary] summary error', err);
              return of({ progress: '', text: this.#translate.instant('AI_SUMMARY_ERROR'), array: [], markdown: false } as SummaryType);
            }),
          )
        : of({ progress: '', text: '', array: [], markdown: false } as SummaryType);
    }),
    // Errors are caught per-emission inside switchMap (the inner catchError above), so the
    // shared source keeps emitting on error — the regenerate driver stays live for the
    // component's lifetime and shareReplay continues to fan out new emissions.
    tap(summary => {
      this.loading = false;
      this.regenerating = false;
      this.showDisclaimer = summary.showDisclaimer ?? false;
      this.generatedAt = summary.generatedAt ?? null;
      this.responseId = summary.responseId ?? null;
      this.usage = summary.usage ?? null;
      this.#cd.markForCheck();
      requestAnimationFrame(() => {
        this.#evaluateReadMore();
        this.#cd.markForCheck();
      });
    }),
    shareReplay(1),
  );

  /** Generate the AI Summary as factual prose via the dedicated summary-brief agent, rendered as markdown. */
  async #loadSummary(kind: RecordKind, recordId: string, force = false, offline = false): Promise<SummaryType> {
    // No summary-brief agent configured for this kind → show a clean empty state instead of
    // doing wasted context work and surfacing a generic error (parity with nova-voice,
    // which gates its surface on availability).
    if (!this.#recordBrief.isAvailable(kind)) {
      return { progress: '', text: '', array: [], markdown: false };
    }
    // Spotlight is territory-scoped: grounding gets no record id, but the RecordRef still needs
    // a stable id for the conversation key.
    const ref: RecordRef = { kind, id: recordId || 'spotlight', label: '' };
    // The summary agent returns a card_response; today it carries a single factual-prose text
    // section, so render its prose. Further section types would render here as the card grows.
    // The transport assembles grounding from this surface's agent-config row (category 6/7/8);
    // brief() manages its own conversation.
    // Offline the agent is unreachable, so the card shows the brief already persisted for this
    // record; with none stored the empty text below drives the offline notice.
    const result = offline
      ? await this.#recordBrief.cachedBrief(ref)
      : await this.#recordBrief.brief(ref, { force });
    if (!result) return { progress: '', text: '', array: [], markdown: false };
    const { card, generatedAt, responseId, usage } = result;
    const prose = card.sections
      .filter((s): s is Extract<Section, { type: 'text' }> => s.type === 'text')
      .map(s => s.text)
      .join('\n\n');
    const html = await marked(prose);
    return {
      progress: '', text: html, array: [html], markdown: false, showDisclaimer: true, generatedAt,
      responseId: responseId ?? null, usage: usage ?? null,
    };
  }

  ngOnDestroy() {
    this.#ngDestroy$.next();
    this.#ngDestroy$.complete();
  }

  private updateState(newState: ComponentState) {
    this.#currentState = newState;
    this.#stateSubject.next(newState);
    this.#cd.markForCheck();
  }

  ngOnInit() {
    // Spotlight (no ID): Start in minimized state
    // Profile (has ID): Start in collapsed state
    const initialState = this.isSpotlight ? 'minimized' : 'collapsed';
    this.#currentState = initialState;
    this.#stateSubject.next(initialState);
    this.#cd.markForCheck();
    this.summary$.pipe(takeUntil(this.#ngDestroy$)).subscribe();
    this.#resizeObserver.pipe(takeUntil(this.#ngDestroy$)).subscribe(() => {
      this.#evaluateReadMore();
      this.#cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['contactId'] || changes['accountId']) {
      // Reset state when inputs change
      const newState = this.isSpotlight ? 'minimized' : 'collapsed';
      this.updateState(newState);
    }
  }

  #evaluateReadMore() {
    const el = this.#el.nativeElement.querySelector('.ai-summary-content .clamped-text');
    if (!el) return;
    const noClampEl = this.#el.nativeElement.querySelector('.ai-summary-content .full-text');
    if (!noClampEl) return;

    this.hasReadMore = noClampEl.clientHeight > el.clientHeight;
    this.#cd.markForCheck();
  }

  openSummary() {
    if (!this.type) return;
    if (this.isSpotlight) {
      this.close.emit();
      return;
    }
    if (this.type === 'account' && !this.accountId) return;
    if (this.type === 'contact' && !this.contactId) return;

    // Handle state transitions
    if (this.#currentState === 'collapsed') {
      // Collapsed → Minimized (for both profiles and spotlight)
      this.updateState('minimized');
    } else if (this.#currentState === 'minimized') {
      // Minimized → Collapsed
      this.updateState('collapsed');
    } else if (this.#currentState === 'maximized') {
      // Maximized → Collapsed (for spotlight)
      this.updateState('collapsed');
    }
  }

  toggleMaximize() {
    if (this.#currentState === 'minimized') {
      this.updateState('maximized');
    } else if (this.#currentState === 'maximized') {
      this.updateState('minimized');
    }
  }

  /** "Generated just now" / "Generated 15 minutes ago" for the header, in the app language; empty until a brief loads. */
  get generatedLabel(): string {
    if (!this.generatedAt) return '';
    const then = new Date(this.generatedAt).getTime();
    if (isNaN(then)) return '';
    if (Date.now() - then < 60_000)
      return this.#translate.instant('AI_SUMMARY_GENERATED_JUST_NOW');
    const time = distanceInWordsToNow(then, {
      addSuffix: true,
      ...this.#localization.getDateFnsLocaleObject(),
    });
    return this.#translate.instant('AI_SUMMARY_GENERATED', { time });
  }

  /** Force a fresh brief, bypassing the cache (header refresh button). No-op while collapsed, busy or offline. */
  regenerate() {
    if (this.isCollapsed || this.loading || this.regenerating || this.#deviceService.isOffline) return;
    this.#regenerate$.next();
  }

  /** A thumbs vote from the feedback footer. */
  onFeedback(e: NovaFeedbackEvent): void {
    this.#feedback.submitFeedback(e.responseId, e.up, e.comment);
  }

  @HostListener('document:touchend', ['$event'])
  @HostListener('document:mouseup', ['$event'])
  onClick(event: Event) {
    if (this.#currentState === 'maximized') {
      if (!this.#el.nativeElement.contains(event.target)) {
        this.updateState('minimized');
      }
    }
  }
}
